import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package, Truck, Users, ShoppingCart, TrendingUp, AlertTriangle, ArrowUpDown } from 'lucide-react';
import { dashboardApi } from '../api/api';

function fmt(n) { return Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 }); }
function fmtCur(n) { return '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

const statusBadge = (s) => <span className={`badge badge-${s}`}>{s}</span>;

export default function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ['dashboard'],
     queryFn: dashboardApi.getSummary });

  if (isLoading) return (
    <div className="page-content">
      <div className="topbar"><span className="topbar-title">Dashboard</span></div>
      <div className="spinner" />
    </div>
  );

  const d = data || {};

  return (
    <>
 <h1 className="text-2xl font-bold mb-4">Dashboard</h1>
      <div className="page-content">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon green"><Package /></div>
            <div className="stat-label">Total Items</div>
            <div className="stat-value">{fmt(d.totalItems)}</div>
            <div className="stat-sub"><AlertTriangle size={11} /> {fmt(d.lowStockCount)} low stock</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon blue"><Truck /></div>
            <div className="stat-label">Active Vendors</div>
            <div className="stat-value">{fmt(d.totalVendors)}</div>
            <div className="stat-sub"><ShoppingCart size={11} /> {fmt(d.pendingPOs)} pending POs</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon green"><Users /></div>
            <div className="stat-label">Active Clients</div>
            <div className="stat-value">{fmt(d.totalClients)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon amber"><TrendingUp /></div>
            <div className="stat-label">Issues Value</div>
            <div className="stat-value" style={{fontSize:18}}>{fmtCur(d.totalIssueValue)}</div>
            <div className="stat-sub">PO Value: {fmtCur(d.totalPOValue)}</div>
          </div>
        </div>

        <div className="dash-grid">
          {/* Recent POs */}
          <div className="card">
            <div className="card-header">
              <ShoppingCart size={15} />
              <span className="card-title">Recent Purchase Orders</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th>PO#</th><th>Vendor</th><th>Date</th><th>Amount</th><th>Status</th>
                </tr></thead>
                <tbody>
                  {(d.recentPOs || []).length === 0 && <tr><td colSpan={5}><div className="empty-state"><p>No purchase orders yet</p></div></td></tr>}
                  {(d.recentPOs || []).map(po => (
                    <tr key={po.po_number}>
                      <td className="mono">{po.po_number}</td>
                      <td>{po.vendor_name}</td>
                      <td>{new Date(po.order_date).toLocaleDateString('en-IN')}</td>
                      <td className="amount">{fmtCur(po.total_amount)}</td>
                      <td>{statusBadge(po.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Issues */}
          <div className="card">
            <div className="card-header">
              <ArrowUpDown size={15} />
              <span className="card-title">Recent Material Issues</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th>Issue#</th><th>Client</th><th>Date</th><th>Amount</th><th>Status</th>
                </tr></thead>
                <tbody>
                  {(d.recentIssues || []).length === 0 && <tr><td colSpan={5}><div className="empty-state"><p>No issues yet</p></div></td></tr>}
                  {(d.recentIssues || []).map(mi => (
                    <tr key={mi.issue_number}>
                      <td className="mono">{mi.issue_number}</td>
                      <td>{mi.client_name}</td>
                      <td>{new Date(mi.issue_date).toLocaleDateString('en-IN')}</td>
                      <td className="amount">{fmtCur(mi.total_amount)}</td>
                      <td>{statusBadge(mi.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Low Stock */}
        {(d.lowStock || []).length > 0 && (
          <div className="card" style={{marginTop: 16}}>
            <div className="card-header">
              <AlertTriangle size={15} style={{color: 'var(--red)'}} />
              <span className="card-title" style={{color: 'var(--red)'}}>Low Stock Alerts</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Item Code</th><th>Name</th><th>Current Stock</th><th>Reorder Qty</th><th>Unit</th></tr></thead>
                <tbody>
                  {d.lowStock.map(item => (
                    <tr key={item.item_code}>
                      <td className="mono">{item.item_code}</td>
                      <td><span className="low-stock-badge" />{item.name}</td>
                      <td style={{color: 'var(--red)', fontWeight: 600}}>{item.stock_qty}</td>
                      <td>{item.reorder_qty}</td>
                      <td>{item.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}